import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, InputAdornment, TextField, Tooltip, type TextFieldProps } from '@mui/material';
import HelpOutline from '@mui/icons-material/HelpOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// Stylis drops ::-ms-reveal, so a plain style tag is what actually hides Edge's own reveal.
const edgeRevealStyle = '.password-field input::-ms-reveal { display: none; }';

type PasswordFieldProps = TextFieldProps & {
  rules?: readonly string[];
};

export default function PasswordField({ rules, label, InputProps, ...rest }: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [rulesSpacer, setRulesSpacer] = useState<HTMLElement | null>(null);
  const labelText = typeof label === 'string' ? label : 'Password';

  useEffect(() => {
    // A failed login and a dialog save error both leave this field mounted.
    const form = inputRef.current?.form;
    if (!form) return undefined;
    const hide = () => setVisible(false);
    form.addEventListener('submit', hide);
    return () => form.removeEventListener('submit', hide);
  }, []);

  useLayoutEffect(() => {
    if (rules === undefined) return undefined;
    const spacer = inputRef.current
      ?.closest('.password-field')
      ?.querySelector('.MuiInputLabel-root .password-rules-spacer');
    setRulesSpacer(spacer instanceof HTMLElement ? spacer : null);
    return undefined;
  }, [rules]);

  return (
    <>
      <style>{edgeRevealStyle}</style>
      <TextField
        {...rest}
        className="password-field"
        label={
          rules === undefined ? (
            label
          ) : (
            <>
              {labelText}
              {/* The notch clones this label. The spacer is not focusable; the button is portaled into the visible one only. */}
              <Box
                component="span"
                className="password-rules-spacer"
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  width: 20,
                  height: '1em',
                  verticalAlign: 'middle',
                }}
              />
            </>
          )
        }
        type={visible ? 'text' : 'password'}
        inputRef={inputRef}
        InputProps={{
          ...InputProps,
          // Moving the pointer off the box, e.g. towards Sign in, masks the password again.
          onMouseLeave: event => {
            setVisible(false);
            InputProps?.onMouseLeave?.(event);
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                aria-label={visible ? 'Hide password' : 'Show password'}
                onClick={() => setVisible(isVisible => !isVisible)}
                onMouseDown={event => event.preventDefault()}
                edge="end"
              >
                {visible ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {rules === undefined || rulesSpacer === null
        ? null
        : createPortal(
            <Tooltip
              enterTouchDelay={0}
              title={
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {rules.map(rule => (
                    <li key={rule}>{rule}</li>
                  ))}
                </Box>
              }
            >
              <IconButton
                type="button"
                size="small"
                aria-label="Password rules"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  minWidth: 0,
                  minHeight: 0,
                  p: 0,
                  bgcolor: 'background.paper',
                  pointerEvents: 'auto',
                }}
              >
                <HelpOutline sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>,
            rulesSpacer
          )}
    </>
  );
}
